'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { signIn } from 'next-auth/react';
import { useEffect, useState } from 'react';
import RichText from '@/components/atoms/rich-text/rich-text';
import Spinner from '@/components/atoms/spinner/spinner';
import BenefitsModule from '@/components/modules/benefit-module/benefit-module';
import LoginCurtainInvalidStep from '@/components/organisms/login-curtain/login-curtain-invalid-step/login-curtain-invalid-step';
import LoginCurtainSignupStep from '@/components/organisms/login-curtain/login-curtain-signup-step/login-curtain-signup-step';
import LoginCurtainVerificationStep from '@/components/organisms/login-curtain/login-curtain-verification-step/login-curtain-verification-step';
import { Module } from '@/components/utilities/content-modules/module-list/module-list';
import {
  trackPageLoadEvent,
  trackViewEvent,
} from '@/integrations/tracking/google-tag-manager/scripts';
import { tealiumView } from '@/integrations/tracking/tealium/scripts';
import { LoginContextProvider } from '@/libraries/contexts/login-context';
import { updateUrlWithParameters } from '@/libraries/formatters/update-url-with-parameters';
import styles from './sign-in-sign-up-flow.module.scss';

export interface SignInSignUpFlowProps {
  /** An array of the content modules to show */
  contentModules: Module[];
  /** The help text to show at the bottom */
  helpText?: string | null;
  /** Personalization data for the user */
  personalizationData?: Content.Properties.PersonalizationData;
  /** Tracking data for the page */
  trackingData?: Content.Properties.TrackingData;
}

const getSafeReturnTo = (returnTo: string | null) => {
  if (!returnTo || !returnTo.startsWith('/') || returnTo.startsWith('//')) {
    return null;
  }

  return returnTo;
};

const SignInSignUpFlow = ({
  contentModules = [],
  personalizationData,
  trackingData,
  helpText,
}: SignInSignUpFlowProps) => {
  const [step, setStep] = useState<'initial' | 'verification' | 'invalid'>(
    'initial',
  );
  const searchParams = useSearchParams();
  const router = useRouter();
  const [isSigningIn, setIsSigningIn] = useState(() =>
    Boolean(searchParams?.get('token_hash')),
  );
  const signInSignUpModule = (
    contentModules as Array<
      Module | Content.Components['SignInSignUpFlowModuleDto']
    >
  ).find(
    module =>
      typeof module === 'object' &&
      module !== null &&
      '$type' in module &&
      (module as { $type?: string }).$type === 'SignInSignUpFlowModuleDto',
  ) as Content.Components['SignInSignUpFlowModuleDto'] | undefined;

  const benefitsModule = signInSignUpModule?.benefits.items.find(
    item => item.$type === 'BenefitModuleDto',
  ) as Content.Components['BenefitModuleDto'] | undefined;

  const updateUrl = () => {
    if (!searchParams) return;

    const parametersObject: Record<string, any> = {};

    new URLSearchParams(searchParams.toString()).forEach((value, key) => {
      if (key === 'agencyId' || key === 'token_hash') return;

      parametersObject[key] = value;
    });

    updateUrlWithParameters({
      newParameters: parametersObject,
      overwrite: true,
      router,
      shouldNavigate: false,
    });
  };

  useEffect(() => {
    if (!searchParams) return;

    const tokenHash = searchParams.get('token_hash');

    if (!tokenHash) return;

    setIsSigningIn(true);
    signIn('magic-link', {
      redirect: false,
      tokenHash,
    })
      .then(result => {
        if (result?.error) {
          updateUrl();
          setStep('invalid');
          setIsSigningIn(false);

          return;
        }

        const redirectPath =
          getSafeReturnTo(searchParams.get('returnTo')) || '/';

        router.replace(redirectPath);
      })
      .catch(() => {
        updateUrl();
        setStep('invalid');
        setIsSigningIn(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    trackPageLoadEvent({ content_group: 'sign_in_sign_up_flow' });

    personalizationData && tealiumView(personalizationData);

    if (!trackingData?.gtmDataLayer) return;

    trackViewEvent(trackingData.gtmDataLayer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.component}>
      <div className={styles.wrapper}>
        {isSigningIn ? (
          <Spinner />
        ) : (
          signInSignUpModule && (
            <LoginContextProvider>
              <div className={styles.container}>
                <div className={styles.main}>
                  {step === 'initial' ? (
                    <LoginCurtainSignupStep
                      {...signInSignUpModule.signUp}
                      onComplete={() => {
                        setStep('verification');
                      }}
                    />
                  ) : step === 'verification' ? (
                    <LoginCurtainVerificationStep
                      {...signInSignUpModule.verification}
                    />
                  ) : (
                    <LoginCurtainInvalidStep
                      {...signInSignUpModule.invalid}
                      onClick={() => setStep('initial')}
                    />
                  )}
                </div>

                <div className={styles.footer}>
                  <div className={styles.benefits}>
                    {benefitsModule && (
                      <BenefitsModule
                        benefits={benefitsModule.benefits}
                        className={styles.benefitItems}
                        headline={benefitsModule.headline}
                      />
                    )}
                  </div>
                </div>
              </div>

              {helpText && (
                <div className={styles.helpTextContainer}>
                  <RichText className={styles.helpText} text={helpText} />
                </div>
              )}
            </LoginContextProvider>
          )
        )}
      </div>
    </div>
  );
};

SignInSignUpFlow.displayName = 'SignInSignUpFlow';

export default SignInSignUpFlow;
